const EMPTY = Object.freeze({});

export function restoreChatRunMessages(history, run) {
  if (!run) return history;
  if (run.active) return run.messages;
  const final = [...run.messages].reverse().find(message => message?.role === 'assistant');
  if (!final || final.pending) return history;
  const index = history.findIndex(message => String(message.id) === String(final.serverId || final.id)
    || (message.role === 'assistant' && message.content === final.content
      && new Date(message.timestamp).getTime() >= run.startedAt - 1000));
  if (index >= 0) {
    if (history[index].pending) return history.map((message, i) => i === index ? final : message);
    return history.map((message, i) => i === index
      ? { ...final, ...message, trace: message.trace || final.trace }
      : message);
  }
  const latestAt = new Date(history[history.length - 1]?.timestamp || 0).getTime();
  // A response/error may arrive before its server row is persisted. Do not
  // replace it with the older history window when returning to this chat.
  return latestAt > run.finishedAt ? history : run.messages;
}

// Transport lifetimes belong to an account + conversation, not a mounted page.
export function createChatRunStore() {
  let owner = '';
  let generation = 0;
  const runs = new Map();
  const snapshots = new Map();
  const listeners = new Set();
  let indicators = EMPTY;
  const notify = () => listeners.forEach((listener) => listener());
  const owns = (run) => !!run && run.owner === owner && run.generation === generation;
  const isCurrent = (run) => owns(run) && runs.get(run.conversationId) === run && run.active;
  const publish = (run) => {
    snapshots.set(run.conversationId, { ...run });
    const next = {};
    runs.forEach((item, cid) => {
      if (item.active || item.status === 'error') {
        next[cid] = { status: item.status, label: item.label, requestId: item.requestId };
      }
    });
    if (JSON.stringify(indicators) !== JSON.stringify(next)) indicators = next;
    notify();
  };
  return {
    subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); },
    setOwner(value) {
      const next = String(value || '');
      if (owner === next) return;
      generation += 1;
      owner = next;
      runs.forEach((run) => {
        run.stopRequested = true;
        run.cancelled = true;
        run.controller?.abort();
      });
      runs.clear();
      snapshots.clear();
      indicators = EMPTY;
      notify();
    },
    owns,
    isCurrent,
    get(account, cid) {
      const run = owner === account ? runs.get(Number(cid)) : null;
      return run?.active ? run : null;
    },
    getSnapshot(account, cid) { return owner === account ? snapshots.get(Number(cid)) || null : null; },
    getIndicators(account) { return owner === account ? indicators : EMPTY; },
    start(account, details) {
      const cid = Number(details.conversationId);
      if (!account || account !== owner || !Number.isFinite(cid) || cid <= 0 || runs.get(cid)?.active) return null;
      const run = {
        ...details, owner, generation, conversationId: cid,
        active: true, cancelled: false, stopRequested: false,
        status: 'running', label: details.label || '',
        messages: details.messages || [],
        startedAt: Date.now(), lastProgressAt: Date.now(),
      };
      runs.delete(cid);
      runs.set(cid, run);
      publish(run);
      return run;
    },
    patch(run, changes) {
      if (!isCurrent(run)) return false;
      Object.assign(run, changes, { lastProgressAt: Date.now() });
      publish(run);
      return true;
    },
    updateMessages(run, updater) {
      if (!isCurrent(run)) return null;
      const next = updater(run.messages);
      const assistant = [...next].reverse().find((message) => message?.role === 'assistant');
      run.messages = next;
      run.partialText = String(assistant?.content || '');
      run.label = String(assistant?.pendingLabel || run.label || '');
      run.lastProgressAt = Date.now();
      publish(run);
      return next;
    },
    finish(run) {
      if (!isCurrent(run)) return;
      const assistant = [...run.messages].reverse().find((message) => message?.role === 'assistant');
      run.active = false;
      run.finishedAt = Date.now();
      run.isStoppable = false;
      run.status = assistant?.isError ? 'error' : (assistant?.stopped || run.stopRequested ? 'cancelled' : 'completed');
      if (run.status === 'error') run.label = '';
      // Keep recent finished snapshots for immediate return before history sync.
      const finished = [...runs.values()].filter((item) => !item.active);
      finished.slice(0, Math.max(0, finished.length - 20)).forEach((item) => {
        runs.delete(item.conversationId);
        snapshots.delete(item.conversationId);
      });
      publish(run);
    },
  };
}

export const chatRunStore = createChatRunStore();
